import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Plus, Trash2, Save, X, RefreshCw } from "lucide-react";

const API = typeof window !== "undefined" ? `${window.location.origin}/api` : "/api";
const TOKEN_KEY = "rise_admin_token";

const COURSE_OPTIONS = ["Adult MHFA — Two Day", "MHFA Refresher", "MHFA Aware"];
const FORMAT_OPTIONS = ["London", "Stevenage", "Online"];

const EMPTY_FORM = {
  day: "",
  month: "2026",
  endDate: "",
  course: "Adult MHFA — Two Day",
  format: "London",
  timings: "09:15 – 17:00",
  price: "£325",
  bookingUrl: "",
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [signingIn, setSigningIn] = useState(false);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const authHeader = () => (token ? { Authorization: `Bearer ${token}` } : {});

  useEffect(() => {
    if (!token) return;
    // Confirm token still valid
    (async () => {
      try {
        await axios.get(`${API}/admin/me`, { headers: authHeader() });
      } catch (e) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast.error("Your session expired. Please sign in again.");
      }
    })();
  }, [token]);

  const loadCourses = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/upcoming-courses`, {
        params: { include_past: true },
      });
      setCourses(data);
    } catch (e) {
      toast.error("Could not load courses.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadCourses();
  }, [token]);

  const onLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setSigningIn(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
      toast.success("Signed in.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sign-in failed.");
    } finally {
      setSigningIn(false);
    }
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setCourses([]);
    toast.success("Signed out.");
  };

  const startEdit = (course) => {
    setEditingId(course.id);
    setForm({
      day: course.day,
      month: course.month,
      endDate: course.endDate,
      course: course.course,
      format: course.format,
      timings: course.timings,
      price: course.price,
      bookingUrl: course.bookingUrl || "",
    });
    window.scrollTo({ top: 300, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.day || !form.month || !form.endDate || !form.course || !form.format || !form.timings || !form.price) {
      toast.error("Please fill in every field.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/upcoming-courses/${editingId}`, form, { headers: authHeader() });
        toast.success("Course updated.");
      } else {
        await axios.post(`${API}/upcoming-courses`, form, { headers: authHeader() });
        toast.success("Course added.");
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      await loadCourses();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast.error("Session expired — please sign in again.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not save.");
      }
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (course) => {
    if (!window.confirm(`Delete the ${course.course} on ${course.day} ${course.month}?`)) return;
    try {
      await axios.delete(`${API}/upcoming-courses/${course.id}`, { headers: authHeader() });
      toast.success("Course deleted.");
      await loadCourses();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not delete.");
    }
  };

  const onFormChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  // ---- Login screen ----
  if (!token) {
    return (
      <div className="pt-36 pb-24 min-h-screen">
        <div className="container-x max-w-md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-[#F3EFEA] rounded-3xl p-8 md:p-12"
          >
            <div className="flex items-center gap-3 mb-8">
              <span className="w-12 h-12 rounded-full bg-[#4A5D52] text-[#FDFBF7] flex items-center justify-center">
                <Lock className="w-5 h-5" strokeWidth={1.6} />
              </span>
              <div>
                <p className="eyebrow text-[#D88C76]">Rise Learning</p>
                <h1 className="font-serif-display text-3xl text-[#1E2A25]">Admin sign in</h1>
              </div>
            </div>

            <form onSubmit={onLogin} data-testid="admin-login-form" className="space-y-6">
              <div className="flex flex-col gap-2">
                <label className="eyebrow text-[#4A5D52]">Password</label>
                <input
                  type="password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-password-input"
                  className="bg-transparent border-b border-[#4A5D52]/30 py-3 text-[#1E2A25] focus:border-[#4A5D52] outline-none transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={signingIn}
                data-testid="admin-signin-btn"
                className="btn-primary w-full justify-center disabled:opacity-60"
              >
                {signingIn ? "Signing in…" : "Sign in"}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    );
  }

  // ---- Dashboard ----
  return (
    <div data-testid="admin-dashboard" className="pt-36 pb-24">
      <div className="container-x">
        <div className="flex flex-wrap justify-between items-end gap-4 mb-10">
          <div>
            <p className="eyebrow mb-2">Admin dashboard</p>
            <h1 className="font-serif-display text-4xl md:text-5xl text-[#1E2A25]">Upcoming open courses</h1>
          </div>
          <div className="flex gap-3">
            <button onClick={loadCourses} data-testid="admin-refresh-btn" className="btn-secondary">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button onClick={onLogout} data-testid="admin-logout-btn" className="btn-secondary">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>

        {/* Add / Edit form */}
        <form
          onSubmit={onSubmit}
          data-testid="admin-course-form"
          className="bg-[#F3EFEA] rounded-3xl p-6 md:p-8 mb-10"
        >
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-serif-display text-2xl text-[#1E2A25]">
              {editingId ? "Edit course date" : "Add a new course date"}
            </h2>
            {editingId && (
              <button type="button" onClick={cancelEdit} className="link-underline text-sm">
                <X className="w-4 h-4" /> Cancel edit
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Field label="Course *" name="course" value={form.course} onChange={onFormChange} type="select" options={COURSE_OPTIONS} testId="admin-input-course" />
            <Field label="Venue / format *" name="format" value={form.format} onChange={onFormChange} type="select" options={FORMAT_OPTIONS} testId="admin-input-format" />
            <Field label="Day (e.g. 25 & 26 Feb) *" name="day" value={form.day} onChange={onFormChange} testId="admin-input-day" />
            <Field label="Month/Year (e.g. 2026) *" name="month" value={form.month} onChange={onFormChange} testId="admin-input-month" />
            <Field label="End date (YYYY-MM-DD) *" name="endDate" value={form.endDate} onChange={onFormChange} type="date" testId="admin-input-enddate" />
            <Field label="Timings *" name="timings" value={form.timings} onChange={onFormChange} testId="admin-input-timings" />
            <Field label="Price *" name="price" value={form.price} onChange={onFormChange} testId="admin-input-price" />
            <div className="md:col-span-4">
              <Field
                label="Booking URL (optional — leave blank to route to Contact page)"
                name="bookingUrl"
                value={form.bookingUrl}
                onChange={onFormChange}
                testId="admin-input-bookingurl"
              />
              <p className="mt-2 text-xs text-[#4A5D52]/80 italic">
                When set, the &quot;Book&quot; button on the public site will open this URL in a new tab
                (e.g. an MHFA England booking link). Leave blank to send visitors to the enquiry
                page with the course prefilled.
              </p>
            </div>
          </div>
          <button type="submit" disabled={saving} data-testid="admin-save-btn" className="btn-primary mt-8 disabled:opacity-60">
            {saving ? "Saving…" : editingId ? (<><Save className="w-4 h-4" /> Save changes</>) : (<><Plus className="w-4 h-4" /> Add course</>)}
          </button>
          <p className="mt-3 text-xs text-[#4A5D52]/80 italic">
            Tip: courses whose "End date" is before today are automatically hidden on the public website.
          </p>
        </form>

        {/* Course list */}
        <div className="rounded-3xl border border-[#8DA399]/30 overflow-hidden bg-[#FDFBF7]">
          <div className="hidden md:grid grid-cols-12 gap-4 items-center px-8 py-4 bg-[#F3EFEA] border-b border-[#8DA399]/25 text-xs uppercase tracking-[0.2em] text-[#4A5D52]">
            <div className="col-span-2">Date</div>
            <div className="col-span-3">Course</div>
            <div className="col-span-2">Venue</div>
            <div className="col-span-2">Timings</div>
            <div className="col-span-1">Price</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {loading ? (
            <div className="p-10 text-center text-[#4A5D52]">Loading…</div>
          ) : courses.length === 0 ? (
            <div className="p-10 text-center text-[#4A5D52]">No courses yet — add one above.</div>
          ) : (
            courses.map((c, i) => {
              const isPast = new Date(c.endDate) < new Date(new Date().toDateString());
              return (
                <div
                  key={c.id}
                  data-testid={`admin-row-${i}`}
                  className={`grid grid-cols-1 md:grid-cols-12 gap-4 items-center px-6 md:px-8 py-5 ${i !== courses.length - 1 ? "border-b border-[#8DA399]/20" : ""} ${isPast ? "opacity-50" : ""}`}
                >
                  <div className="md:col-span-2">
                    <div className="font-serif-display text-lg text-[#1E2A25]">{c.day}</div>
                    <div className="text-xs text-[#4A5D52]">{c.month}{isPast ? " · past" : ""}</div>
                  </div>
                  <div className="md:col-span-3 text-[#1E2A25]">{c.course}</div>
                  <div className="md:col-span-2 text-sm text-[#4A5D52]">{c.format}</div>
                  <div className="md:col-span-2 text-sm text-[#4A5D52]">{c.timings}</div>
                  <div className="md:col-span-1 text-[#1E2A25] font-medium">{c.price}</div>
                  <div className="md:col-span-2 flex md:justify-end gap-2">
                    <button
                      onClick={() => startEdit(c)}
                      data-testid={`admin-edit-${i}`}
                      className="px-3 py-1.5 rounded-full text-sm border border-[#4A5D52]/30 hover:bg-[#4A5D52] hover:text-[#FDFBF7] hover:border-[#4A5D52] transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => onDelete(c)}
                      data-testid={`admin-delete-${i}`}
                      className="w-8 h-8 rounded-full border border-[#D88C76]/40 text-[#D88C76] hover:bg-[#D88C76] hover:text-[#FDFBF7] flex items-center justify-center transition-colors"
                      aria-label="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", options, testId }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="eyebrow text-[#4A5D52]">{label}</label>
      {type === "select" ? (
        <select
          name={name}
          value={value}
          onChange={onChange}
          data-testid={testId}
          className="bg-transparent border-b border-[#4A5D52]/30 py-3 text-[#1E2A25] focus:border-[#4A5D52] outline-none transition-colors"
        >
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          data-testid={testId}
          className="bg-transparent border-b border-[#4A5D52]/30 py-3 text-[#1E2A25] focus:border-[#4A5D52] outline-none transition-colors"
        />
      )}
    </div>
  );
}
